import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout as AntLayout,
  Menu,
  Avatar,
  Dropdown,
  Space,
  Typography,
  theme,
  Button,
  Drawer,
  Grid,
  message,
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  TeamOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  KeyOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  CarOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useUser } from '../contexts/UserContext';
import { useSystem } from '../contexts/SystemContext';
import ChangePasswordModal from './ChangePasswordModal';

const { Header, Sider, Content } = AntLayout;
const { Title } = Typography;
const { useBreakpoint } = Grid;

const Layout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { user, logout } = useUser();
  const { systemSettings } = useSystem();
  const [messageApi, contextHolder] = message.useMessage();
  
  // 确保messageApi被使用，防止webpack优化
  console.log('messageApi available:', !!messageApi);
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // 默认展开侧边栏（桌面端和移动端都展开）
  // useEffect(() => {
  //   if (isMobile) {
  //     setCollapsed(true);
  //   }
  // }, [isMobile]);

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
    },
    {
      key: '/basic-data',
      icon: <DatabaseOutlined />,
      label: '基础数据',
      children: [
        {
          key: '/shipping-stations',
          icon: <EnvironmentOutlined />,
          label: '收发货站点',
        },
        {
          key: '/consigners',
          icon: <UserOutlined />,
          label: '收发货人管理',
        },
        {
          key: '/carriers',
          icon: <CarOutlined />,
          label: '承运单位管理',
        },
      ],
    },
    {
      key: '/logistics',
      icon: <CarOutlined />,
      label: '物流收发集运',
      children: [
        {
          key: '/logistics/new-order',
          icon: <PlusOutlined />,
          label: '新增物流收货',
        },
        {
          key: '/logistics/orders',
          icon: <DatabaseOutlined />,
          label: '物流单列表',
        },
      ],
    },
    {
      key: '/employees',
      icon: <UserOutlined />,
      label: '员工管理',
      children: [
        {
          key: '/employees',
          icon: <UserOutlined />,
          label: '员工管理',
        },
      ],
    },
    {
      key: '/system-settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      children: [
        {
          key: '/system-settings/basic-settings',
          icon: <SettingOutlined />,
          label: '基本设置',
        },
        {
          key: '/system-settings/device-management',
          icon: <SettingOutlined />,
          label: '设备管理',
        },
      ],
    },
    {
      key: '/about',
      icon: <TeamOutlined />,
      label: '关于我们',
    },
  ];

  const userMenuItems = [
    {
      key: 'changePassword',
      icon: <KeyOutlined />,
      label: '修改密码',
      onClick: () => setChangePasswordVisible(true),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    if (isMobile) {
      setMobileDrawerVisible(false);
    }
  };

  const renderSidebar = () => (
    <>
      <div style={{ 
        height: 32, 
        margin: 16, 
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 'bold'
      }}>
        {collapsed ? 'WL' : (systemSettings?.company_short_name || '物流系统')}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
      />
    </>
  );

  return (
    <>
      {contextHolder}
      <AntLayout style={{ minHeight: '100vh' }}>
        {/* 桌面端侧边栏 */}
      {!isMobile && (
        <Sider trigger={null} collapsible collapsed={collapsed}>
          {renderSidebar()}
        </Sider>
      )}
      
      <AntLayout>
        <Header style={{ 
          padding: isMobile ? '0 12px' : '0 16px', 
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: isMobile ? '56px' : '64px'
        }}>
          <Space>
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileDrawerVisible(true)}
                style={{ fontSize: '18px' }}
              />
            ) : (
              React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                className: 'trigger',
                onClick: () => setCollapsed(!collapsed),
                style: { fontSize: '18px' }
              })
            )}
            <Title 
              level={isMobile ? 5 : 4} 
              style={{ 
                margin: 0,
                fontSize: isMobile ? '16px' : '20px',
                lineHeight: isMobile ? '24px' : '28px'
              }}
            >
              {isMobile ? (systemSettings?.company_short_name || '物流系统') : (systemSettings?.system_name || '智能物流管理系统')}
            </Title>
          </Space>
          <Dropdown
            menu={{
              items: userMenuItems,
            }}
            placement="bottomRight"
          >
            <Space style={{ cursor: 'pointer' }}>
              <Avatar 
                icon={<UserOutlined />} 
                size={isMobile ? 'small' : 'default'}
              />
              {!isMobile && <span>{user?.realName || user?.username || '管理员'}</span>}
            </Space>
          </Dropdown>
        </Header>
        <Content
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 16 : 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <Outlet />
        </Content>
      </AntLayout>

      {/* 移动端抽屉菜单 */}
      <Drawer
        title="菜单"
        placement="left"
        onClose={() => setMobileDrawerVisible(false)}
        open={mobileDrawerVisible}
        width={280}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ 
          height: 32, 
          margin: 16, 
          background: 'rgba(0, 0, 0, 0.1)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold'
        }}>
          {systemSettings?.company_short_name || '物流系统'}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ border: 'none' }}
          theme="light"
        />
      </Drawer>

      {/* 修改密码模态框 */}
      <ChangePasswordModal
        visible={changePasswordVisible}
        onCancel={() => setChangePasswordVisible(false)}
        onSuccess={() => {
          // 密码修改成功后的回调
        }}
      />
      </AntLayout>
    </>
  );
};

export default Layout;
